'use client';

import { useState, useEffect, useCallback } from 'react';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';
import { DocumentArrowDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export interface DownloadFilePageProps {
  token?: string | null;
  language?: string;
  translations?: Record<string, string>;
}

/**
 * Dosya / broşür indirme sayfası.
 * URL: /brochure/download/{token} veya /brosur/indir/{token}
 * Token ile backend'den PDF indirilir veya doğrulama sonrası indirme linki gösterilir.
 */
export default function DownloadFilePage({
  token: propToken,
  language,
  translations = {},
}: DownloadFilePageProps) {
  const { getVariable } = useLanguageVariables();
  const t = (key: string, fallback: string) =>
    translations[key] ?? getVariable(key, fallback) ?? fallback;

  const [token, setToken] = useState<string | null>(propToken ?? null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  // Token'ı URL'den al (slugPath son segment olarak da gelebilir)
  useEffect(() => {
    if (propToken) {
      setToken(propToken);
      return;
    }
    if (typeof window !== 'undefined') {
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      const downloadIndex = pathParts.findIndex(
        (p) => p === 'brochure' || p === 'brosur' || p === 'download' || p === 'indir'
      );
      if (downloadIndex !== -1 && pathParts[downloadIndex + 1]) {
        setToken(pathParts[downloadIndex + 1]);
      } else if (pathParts.length > 0) {
        const last = pathParts[pathParts.length - 1];
        if (last && last.length > 10) setToken(last);
      }
    }
  }, [propToken]);

  const handleDownload = useCallback(async () => {
    if (!token) {
      setStatus('error');
      setMessage(t('download_file.invalid_link', 'Invalid or missing download link.'));
      return;
    }
    setStatus('loading');
    setMessage(t('download_file.verifying', 'Verifying...'));
    try {
      // Frontend -> Next.js API -> Backend (backend adresi tarayıcıya gönderilmez; server-side proxy)
      const url = `/api/brochure-download/download-by-token/${encodeURIComponent(token)}`;
      const res = await fetch(url, { method: 'GET' });
      if (!res.ok) {
        if (res.status === 404) {
          setMessage(t('download_file.expired', 'This link has expired or is invalid.'));
        } else {
          setMessage(t('download_file.error', 'Unable to download file. Please try again later.'));
        }
        setStatus('error');
        return;
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition');
      const filenameMatch = disposition?.match(/filename="?([^";]+)"?/);
      const filename = filenameMatch?.[1] ?? `brochure-${token.slice(0, 8)}.pdf`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      setStatus('success');
      setMessage(t('download_file.success', 'Your file has been downloaded.'));
    } catch {
      setStatus('error');
      setMessage(t('download_file.error', 'Unable to download file. Please try again later.'));
    }
  }, [token, t]);

  useEffect(() => {
    if (token && status === 'idle') {
      handleDownload();
    }
  }, [token, status, handleDownload]);

  if (!token) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-neutral-200/90 p-8 text-center">
          <ExclamationTriangleIcon className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {t('download_file.invalid_link_title', 'Invalid link')}
          </h2>
          <p className="text-gray-600">
            {t('download_file.invalid_link', 'Invalid or missing download link. Please use the link from your email.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[50vh] flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-neutral-200/90 p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="inline-flex w-16 h-16 rounded-full bg-alsero-first/10 items-center justify-center mb-4">
              <div className="w-8 h-8 border-2 border-alsero-first border-t-transparent rounded-full animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {t('download_file.preparing', 'Preparing your file...')}
            </h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="inline-flex w-16 h-16 rounded-full bg-green-100 items-center justify-center mb-4">
              <DocumentArrowDownIcon className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {t('download_file.success_title', 'Download complete')}
            </h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              type="button"
              onClick={handleDownload}
              className="px-6 py-3 bg-alsero-first text-white rounded-lg font-medium hover:bg-alsero-first/90 transition-colors"
            >
              {t('download_file.download_again', 'Download again')}
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <ExclamationTriangleIcon className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {t('download_file.error_title', 'Download failed')}
            </h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              type="button"
              onClick={() => { setStatus('idle'); handleDownload(); }}
              className="px-6 py-3 bg-alsero-first text-white rounded-lg font-medium hover:bg-alsero-first/90 transition-colors"
            >
              {t('download_file.retry', 'Try again')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
